// client/src/components/StandardTemplateForm.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { inputStyle, labelStyle, sectionStyle, META_LIMITS } from './templateStyles';
import { extractVars, buildStandardConfig } from '../utils/templateConfig';
import { useIsMobile } from '../hooks/useMediaQuery';
import VariableEditor from './VariableEditor';

function StandardTemplateForm({ token, channelId, language, category, name, customFields }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerType, setHeaderType] = useState('TEXT');
  const [headerText, setHeaderText] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerMediaName, setHeaderMediaName] = useState('');
  const [headerUploading, setHeaderUploading] = useState(false);

  const [bodyText, setBodyText] = useState('');
  const [variants, setVariants] = useState({});

  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerText, setFooterText] = useState('');

  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [buttons, setButtons] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const detectedVars = extractVars(bodyText);

  const handleBodyChange = (text) => {
    setBodyText(text);
    const newVars = extractVars(text);
    setVariants((prev) => {
      const updated = {};
      newVars.forEach((v) => { updated[v] = prev[v] || { type: 'fixed', value: '' }; });
      return updated;
    });
  };

  const handleUploadMedia = async (file) => {
    setHeaderUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/templates/upload-media', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setHeaderMediaUrl(data.url);
      setHeaderMediaName(file.name);
    } catch (err) {
      setError(`Error al subir archivo: ${err.message}`);
    } finally {
      setHeaderUploading(false);
    }
  };

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons((prev) => [...prev, { type: 'QUICK_REPLY', text: '', payload: '', url: '', phone: '' }]);
  };
  const removeButton = (idx) => setButtons((prev) => prev.filter((_, i) => i !== idx));
  const updateButton = (idx, field, value) =>
    setButtons((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!channelId) return setError('Selecciona un canal');
    if (!name.trim()) return setError('El nombre es requerido');
    if (!bodyText.trim()) return setError('El cuerpo del mensaje es requerido');
    setLoading(true);
    try {
      const config = buildStandardConfig({
        headerEnabled, headerType, headerText, headerMediaUrl, origin: window.location.origin,
        bodyText, variants, footerEnabled, footerText, buttonsEnabled, buttons,
      });
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), language, category, channel_id: Number(channelId), config, variants }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.message || 'Error al guardar la plantilla');
      navigate('/plantillas', { state: { created: true } });
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (label, enabled, onToggle) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: enabled ? '1rem' : 0 }}>
      <span style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem' }}>{label}</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)}
          style={{ accentColor: theme.colors.primary, width: '16px', height: '16px' }} />
        <span style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>Activar</span>
      </label>
    </div>
  );

  const typeBtn = (label, value, current, setter) => (
    <button key={value} type="button" onClick={() => setter(value)} style={{
      padding: '6px 14px', borderRadius: '6px',
      border: `1px solid ${current === value ? theme.colors.primary : theme.colors.border}`,
      backgroundColor: current === value ? theme.colors.primary : 'transparent',
      color: current === value ? theme.colors.background : theme.colors.text,
      cursor: 'pointer', fontSize: '0.82rem', fontWeight: current === value ? '700' : '400',
    }}>{label}</button>
  );

  return (
    <form onSubmit={handleSubmit}>
      {/* Header */}
      <div style={sectionStyle}>
        {toggleRow('ENCABEZADO', headerEnabled, setHeaderEnabled)}
        {headerEnabled && (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {[['Texto', 'TEXT'], ['Imagen', 'IMAGE'], ['Video', 'VIDEO'], ['Documento', 'DOCUMENT']].map(
                ([label, val]) => typeBtn(label, val, headerType, setHeaderType)
              )}
            </div>
            {headerType === 'TEXT' ? (
              <input type="text" value={headerText} onChange={(e) => setHeaderText(e.target.value)}
                placeholder="Texto del encabezado (máx. 60 caracteres)" maxLength={META_LIMITS.headerText} style={inputStyle} />
            ) : (
              <div>
                <input type="file" id="header-media" style={{ display: 'none' }}
                  accept={headerType === 'IMAGE' ? 'image/*' : headerType === 'VIDEO' ? 'video/*' : '.pdf,.doc,.docx,.xls,.xlsx'}
                  onChange={(e) => { if (e.target.files[0]) handleUploadMedia(e.target.files[0]); }} />
                <label htmlFor="header-media" style={{
                  display: 'inline-block', padding: '8px 18px', backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary,
                  cursor: headerUploading ? 'not-allowed' : 'pointer', fontSize: '0.88rem',
                }}>
                  {headerUploading ? 'Subiendo...' : headerMediaName || 'Seleccionar archivo'}
                </label>
                {headerMediaUrl && (
                  <p style={{ color: theme.colors.success, fontSize: '0.8rem', marginTop: '0.4rem' }}>✓ {headerMediaName}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Body */}
      <div style={sectionStyle}>
        <p style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem', margin: '0 0 1rem' }}>CUERPO (obligatorio)</p>
        <div style={{ background: theme.colors.glowSubtle, border: `1px solid ${theme.colors.border}`, borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.82rem', lineHeight: 1.6, color: theme.colors.textMuted }}>
          💡 Para usar variables escríbelas entre <strong style={{ color: theme.colors.primary }}>llaves dobles</strong>, ej. <code style={{ color: theme.colors.primary }}>{'Hola {{nombre}}, tu pedido {{numero}} ya está listo'}</code>.
          <br />Puedes poner varias. <strong>No uses corchetes</strong> <code>{'[nombre]'}</code> — Meta no las reconoce como variables.
          <br />Abajo aparecerá cada variable para asignarle un valor fijo o un campo del contacto.
        </div>
        <textarea value={bodyText} onChange={(e) => handleBodyChange(e.target.value)}
          placeholder="Escribe tu mensaje. Usa {{nombre}} para variables dinámicas." rows={5}
          maxLength={META_LIMITS.body}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6' }} />
        <VariableEditor vars={detectedVars} variants={variants} customFields={customFields} onChange={setVariants} idPrefix="std" />
      </div>

      {/* Footer */}
      <div style={sectionStyle}>
        {toggleRow('PIE DE PÁGINA', footerEnabled, setFooterEnabled)}
        {footerEnabled && (
          <input type="text" value={footerText} onChange={(e) => setFooterText(e.target.value)}
            placeholder="Texto del pie de página (máx. 60 caracteres)" maxLength={META_LIMITS.footer} style={inputStyle} />
        )}
      </div>

      {/* Buttons */}
      <div style={sectionStyle}>
        {toggleRow('BOTONES', buttonsEnabled, setButtonsEnabled)}
        {buttonsEnabled && (
          <>
            {buttons.map((btn, idx) => (
              <div key={idx} style={{
                backgroundColor: theme.colors.surface, borderRadius: '8px', padding: '0.8rem 1rem',
                marginBottom: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap',
              }}>
                <div style={isMobile ? { width: '100%' } : undefined}>
                  <label style={labelStyle}>Tipo</label>
                  <select value={btn.type} onChange={(e) => updateButton(idx, 'type', e.target.value)}
                    style={{ ...inputStyle, width: isMobile ? '100%' : '180px', padding: '5px 8px', cursor: 'pointer' }}>
                    <option value="QUICK_REPLY">Respuesta rápida</option>
                    <option value="URL">Ir a URL</option>
                    <option value="PHONE_NUMBER">Llamar teléfono</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={labelStyle}>Texto del botón</label>
                  <input type="text" value={btn.text} onChange={(e) => updateButton(idx, 'text', e.target.value)}
                    placeholder="Texto visible" maxLength={META_LIMITS.buttonText} style={{ ...inputStyle, padding: '5px 8px' }} />
                </div>
                {btn.type === 'QUICK_REPLY' && (
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={labelStyle}>ID / palabra clave (oculto)</label>
                    <input type="text" value={btn.payload} onChange={(e) => updateButton(idx, 'payload', e.target.value)}
                      placeholder="ej: confirmar_cita" maxLength={META_LIMITS.quickReplyPayload} style={{ ...inputStyle, padding: '5px 8px' }} />
                  </div>
                )}
                {btn.type === 'URL' && (
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={labelStyle}>URL</label>
                    <input type="url" value={btn.url} onChange={(e) => updateButton(idx, 'url', e.target.value)}
                      placeholder="https://..." maxLength={META_LIMITS.url} style={{ ...inputStyle, padding: '5px 8px' }} />
                  </div>
                )}
                {btn.type === 'PHONE_NUMBER' && (
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={labelStyle}>Teléfono</label>
                    <input type="tel" value={btn.phone} onChange={(e) => updateButton(idx, 'phone', e.target.value)}
                      placeholder="+57..." maxLength={META_LIMITS.phone} style={{ ...inputStyle, padding: '5px 8px' }} />
                  </div>
                )}
                <button type="button" onClick={() => removeButton(idx)}
                  style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '1.2rem', paddingBottom: '6px' }}>×</button>
              </div>
            ))}
            {buttons.length < 3 && (
              <button type="button" onClick={addButton} style={{
                padding: '7px 16px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
                borderRadius: '7px', color: theme.colors.text, cursor: 'pointer', fontSize: '0.85rem',
              }}>+ Agregar botón</button>
            )}
          </>
        )}
      </div>

      {error && <p style={{ color: theme.colors.error, textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}

      <button type="submit" disabled={loading} style={{
        width: '100%', padding: '12px', backgroundColor: loading ? theme.colors.border : theme.colors.primary,
        color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '1rem',
        fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
      }}>{loading ? 'Guardando...' : 'Guardar plantilla'}</button>
    </form>
  );
}

export default StandardTemplateForm;
